<script setup lang="ts">
    import type { BaseSelectOption } from '~/components/shared/BaseSelect.vue';
    import { AcademyTrainingsListingSortSlug } from '~/types/academy/trainings-listing-sort';

    const { t } = useI18n();
    const { sort } = useAcademyTrainingsListing();

    const sortSlugs = Object.values(AcademyTrainingsListingSortSlug);

    const sortLabels = computed<Record<AcademyTrainingsListingSortSlug, string>>(() => ({
        [AcademyTrainingsListingSortSlug.Newest]: t('academy.trainings.sort.newestFirst'),
        [AcademyTrainingsListingSortSlug.Oldest]: t('academy.trainings.sort.oldestFirst'),
        [AcademyTrainingsListingSortSlug.EventDate]: t('academy.trainings.sort.eventDateAsc'),
        [AcademyTrainingsListingSortSlug.NameAscending]: t('academy.trainings.sort.nameAsc'),
        [AcademyTrainingsListingSortSlug.NameDescending]: t('academy.trainings.sort.nameDesc'),
        [AcademyTrainingsListingSortSlug.MostPoints]: t('academy.trainings.sort.mostPoints')
    }));

    // The `selected` flag also sets the option in the server render, where the select has no value yet.
    const selectOptions = computed<BaseSelectOption[]>(() =>
        sortSlugs.map((slug) => ({
            label: sortLabels.value[slug],
            value: slug,
            selected: slug === sort.value
        }))
    );

    function selectSort(value: string | number) {
        const selectedSlug = sortSlugs.find((slug) => slug === value);

        if (selectedSlug) {
            sort.value = selectedSlug;
        }
    }
</script>

<template>
    <label class="sort-select">
        <span class="label">{{ $t('academy.trainings.sort.label') }}</span>
        <BaseSelect
            id="academy-trainings-listing-sort-select"
            :model-value="sort"
            class="sort-select"
            :aria-label="$t('academy.trainings.sort.label')"
            :options="selectOptions"
            @update:model-value="selectSort"
        />
    </label>
</template>

<style lang="scss" scoped>
    .sort-select {
        position: relative;
        display: flex;
        box-sizing: border-box;

        .label {
            font-size: var(--font-xs);
            position: absolute;
            transform: translateY(-100%);
            z-index: 1;
            line-height: 1;
        }

        :deep(.base-select) {
            width: 100%;

            select {
                width: 100%;
                border: 1px solid var(--filter-border);
                box-sizing: border-box;
                padding-top: 0.625rem;
                padding-bottom: 0.625rem;
                height: auto;
                line-height: normal;
                background-color: var(--white);
            }
        }

        @media (min-width: $br-l) {
            width: auto;
            min-width: 156px;
        }
    }
</style>
